import { useEffect, useMemo, useState } from 'react';

const DEFAULT_ROWS_PER_PAGE = 50;

function getMaxPage(totalRows: number, rowsPerPage: number): number {
  return Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
}

export interface UseFrontendPaginationParams<T> {
  items: T[] | undefined;
  filterRowsBy: (row: T) => boolean;
  resetPageWhen: readonly unknown[];
  rowsPerPage?: number;
}

export function useFrontendPagination<T>({
  items,
  filterRowsBy,
  resetPageWhen,
  rowsPerPage = DEFAULT_ROWS_PER_PAGE,
}: UseFrontendPaginationParams<T>) {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
    // Only reset when filter/context deps change — not when paginating.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetPageWhen);

  const filteredResults = useMemo(
    () => items?.filter(filterRowsBy) ?? [],
    [filterRowsBy, items],
  );

  useEffect(() => {
    const maxPage = getMaxPage(filteredResults.length, rowsPerPage);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredResults, page, rowsPerPage]);

  const rows = useMemo(
    () => filteredResults.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredResults, page, rowsPerPage],
  );

  return { page, setPage, filteredResults, rows, rowsPerPage };
}
